import React, { useEffect, useState } from "react";
import api, { getErrorDetail, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { Trash2 } from "lucide-react";

export default function NewsPanel() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ title: "", title_zh: "", issue_no: "", published_at: format(new Date(), "yyyy-MM-dd") });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function load() {
    try { const { data } = await api.get("/news?limit=100"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!file) { toast.error("Pick a PDF file"); return; }
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") { toast.error("Only PDF files accepted"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("PDF must be under 12 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", meta.title);
      fd.append("title_zh", meta.title_zh || "");
      fd.append("issue_no", meta.issue_no || "");
      fd.append("published_at", meta.published_at);
      fd.append("file", file);
      await api.post("/news", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("News issue published");
      setMeta({ title: "", title_zh: "", issue_no: "", published_at: format(new Date(), "yyyy-MM-dd") });
      setFile(null);
      const input = document.getElementById("news-file-input");
      if (input) input.value = "";
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setUploading(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this issue permanently?")) return;
    try { await api.delete(`/news/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-news-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4 h-fit" data-testid="admin-news-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Upload bi-weekly news</h3>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Issue number</Label>
          <Input value={meta.issue_no} onChange={(e)=>setMeta({...meta, issue_no: e.target.value})} placeholder="e.g. 01, 2026-W48" data-testid="admin-news-issue" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Title (English)</Label>
          <Input required value={meta.title} onChange={(e)=>setMeta({...meta, title: e.target.value})} data-testid="admin-news-title" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Title (中文)</Label>
          <Input value={meta.title_zh} onChange={(e)=>setMeta({...meta, title_zh: e.target.value})} data-testid="admin-news-title-zh" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Published on</Label>
          <Input type="date" required value={meta.published_at} onChange={(e)=>setMeta({...meta, published_at: e.target.value})} data-testid="admin-news-date" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">PDF file (max 12 MB)</Label>
          <input id="news-file-input" type="file" accept="application/pdf" required onChange={(e)=>setFile(e.target.files?.[0] || null)} data-testid="admin-news-file" className="mt-2 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[hsl(var(--muted))] file:px-4 file:py-2 file:text-xs file:tracking-[0.15em] file:uppercase file:text-[hsl(var(--foreground))] hover:file:bg-[hsl(var(--muted))]/70" />
          {file && <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{file.name} · {(file.size/1024).toFixed(0)} KB</p>}
        </div>
        <Button type="submit" disabled={uploading} data-testid="admin-news-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{uploading ? "Uploading…" : "Publish issue"}</Button>
      </form>

      <div className="lg:col-span-7">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-3">Published issues · {items.length}</h3>
        <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
          {items.map((n) => (
            <li key={n.id} data-testid={`admin-news-row-${n.id}`} className="p-4 flex items-start gap-4 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))]">
                  {n.issue_no && <span>Issue {n.issue_no}</span>}
                  <span className="text-[hsl(var(--muted-foreground))]">{n.published_at}</span>
                </div>
                <div className="font-display text-lg tracking-tight mt-1">{n.title}</div>
                {n.title_zh && <div className="text-sm text-[hsl(var(--muted-foreground))]">{n.title_zh}</div>}
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{n.original_filename} · {(n.size_bytes/1024).toFixed(0)} KB</div>
              </div>
              <div className="flex items-center gap-1">
                <a href={`${API_BASE}/news/${n.id}/download`} target="_blank" rel="noopener noreferrer" data-testid={`admin-news-download-${n.id}`} className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">Download</a>
                <Button variant="ghost" size="sm" onClick={()=>del(n.id)} data-testid={`admin-news-delete-${n.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No issues uploaded yet.</li>}
        </ul>
      </div>
    </div>
  );
}
